import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { buildCityLocalBusinessSchema } from "@/lib/structuredData";
import { FinalCTA } from "@/components/home/FinalCTA";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/ui/SectionHeader";
import {
  Phone,
  CheckCircle2,
  Calculator,
  Users,
  FileText,
  Building2,
  Receipt,
  ClipboardList,
  MapPin,
} from "lucide-react";

export interface LocationData {
  slug: string;
  city: string;
  province: string;
  heroTitle: string;
  heroDescription: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  whyLocal: string[];
  localInsight: string;
}

const services = [
  { icon: Calculator, title: "Monthly Bookkeeping", description: "Accurate recording of all transactions, invoices, receipts, and bank reconciliations." },
  { icon: Users, title: "Payroll (EMP201 & EMP501)", description: "Complete payroll processing with monthly EMP201 and bi-annual EMP501 reconciliations." },
  { icon: Receipt, title: "VAT & UIF Declarations", description: "Timely VAT returns and UIF monthly declarations to keep you compliant." },
  { icon: FileText, title: "Annual Financial Statements", description: "Professionally prepared statements that meet SARS requirements." },
  { icon: ClipboardList, title: "Tax Returns (ITR14 & Provisional)", description: "Annual and provisional tax return preparation and submission." },
  { icon: Building2, title: "CIPC Annual Returns", description: "On-time annual return submissions to maintain your company's good standing." },
];

export function LocationPage({ location }: { location: LocationData }) {
  const structuredData = buildCityLocalBusinessSchema(location.city, location.province, location.slug);

  return (
    <Layout>
      <SEO
        title={location.metaTitle}
        description={location.metaDescription}
        canonical={`/bookkeeping-services-${location.slug}`}
        structuredData={structuredData}
      />

      {/* Hero */}
      <section className="py-16 lg:py-24 section-accent">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-accent rounded-full px-4 py-2 mb-6">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">
                {location.city}, {location.province}
              </span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              {location.heroTitle}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              {location.heroDescription}
            </p>
            <Link to="/book-a-call">
              <Button size="lg" className="btn-primary gap-2 text-base px-8 h-14">
                <Phone className="w-5 h-5" />
                Book a Free Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 lg:py-28 section-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-5 gap-12">
              <div className="md:col-span-3">
                <span className="inline-block text-sm font-medium text-primary mb-3 tracking-wide uppercase">
                  Local Expertise
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Professional Bookkeeping in {location.city}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {location.intro}
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {location.localInsight}
                </p>
              </div>
              <div className="md:col-span-2">
                <div className="bg-accent rounded-2xl p-8">
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-4">
                    Why {location.city} Businesses Choose Us
                  </h3>
                  <ul className="space-y-3">
                    {location.whyLocal.map((reason) => (
                      <li key={reason} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 lg:py-28 section-light">
        <div className="container mx-auto px-4">
          <SectionHeader
            label={`Services in ${location.city}`}
            title={`What We Offer ${location.city} Businesses`}
            description={`Comprehensive bookkeeping and compliance services tailored for businesses in ${location.city} and across ${location.province}.`}
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service) => (
              <div key={service.title} className="card-elevated p-8">
                <div className="icon-container mb-6">
                  <service.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages CTA */}
      <section className="py-20 lg:py-28 section-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
              Affordable Packages for {location.city} Businesses
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              From sole proprietors to growing companies — our packages start at just R350/month 
              with all SARS compliance included. No hidden fees.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/packages">
                <Button size="lg" className="btn-primary text-base px-8 h-14">
                  View All Packages
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="btn-outline-primary text-base px-8 h-14">
                  Get a Custom Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </Layout>
  );
}
